import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import Question from '../../components/Question';

// Import rune images
import ansuzImage from '../../../img/runes/image-003.webp';
import berkanoImage from '../../../img/runes/image-017.webp';
import sowiloImage from '../../../img/runes/image-015.webp';

const RunesPreparationLesson = ({ 
  level = 'beginner', 
  onNextLesson, 
  markLessonComplete,
  currentQuestion = 0,
  onQuestionChange,
  currentLesson,
  completedQuestions = [],
  setCompletedQuestions
}) => {
  const sections = [
    {
      id: 'mindset',
      title: 'I. Mental and Spiritual Preparation',
      content: `
        Preparation for runic divination begins with the right mindset. The ancient Germanic peoples approached runic work with reverence and respect, 
        recognizing it as a sacred practice rather than a casual activity.
        
        Mental preparation involves clearing the mind of distractions and focusing on the question at hand. 
        This might involve meditation, prayer, or other practices that help center the practitioner.
        
        Spiritual preparation acknowledges the connection between the rune reader and the divine forces associated with the runes. 
        This doesn't necessarily mean religious belief, but rather an openness to the wisdom and guidance the runes can offer.
      `,
      imageUrl: ansuzImage,
      questions: {
        beginner: {
          question: "How did ancient Germanic peoples approach runic work?",
          options: ["Casually", "With reverence and respect", "As entertainment", "As a job"],
          correctAnswer: "With reverence and respect",
        },
        intermediate: {
          question: "What does mental preparation involve?",
          options: [
            "Clearing the mind of distractions",
            "Memorizing all rune meanings",
            "Learning complex rituals",
            "Reading books about runes"
          ],
          correctAnswer: "Clearing the mind of distractions",
        },
      },
    },
    {
      id: 'space',
      title: 'II. Creating Sacred Space',
      content: `
        Creating a suitable space for runic divination helps establish the right atmosphere for the work. 
        This doesn't require elaborate rituals, but rather a clean, quiet space where you won't be interrupted.
        
        The space should be comfortable and conducive to concentration. Some practitioners choose to include meaningful objects, 
        such as candles, crystals, or representations of the natural world, to help create a sense of connection to the tradition.
        
        The key is that the space feels right for you and supports your intention to work with the runes respectfully and effectively.
      `,
      imageUrl: berkanoImage,
      questions: {
        beginner: {
          question: "What is most important about the divination space?",
          options: ["It must be elaborate", "It should be clean and quiet", "It must be outdoors", "It must be dark"],
          correctAnswer: "It should be clean and quiet",
        },
        intermediate: {
          question: "What should the space feel like?",
          options: [
            "Right for you and supportive of your intention",
            "Exactly like ancient Germanic temples",
            "Filled with expensive objects",
            "Completely empty"
          ],
          correctAnswer: "Right for you and supportive of your intention",
        },
      },
    },
    {
      id: 'tools',
      title: 'III. Preparing Your Runes',
      content: `
        The runes themselves should be treated with care and respect. Whether you're using purchased runes or making your own, 
        they should be kept clean and stored in a suitable container when not in use.
        
        Some practitioners choose to cleanse their runes periodically, using methods like moonlight, sunlight, or smoke. 
        This is a personal choice and should be done in a way that feels meaningful to you.
        
        The container for your runes should protect them from damage and keep them organized. 
        Traditional materials include leather pouches, wooden boxes, or cloth bags, but any container that serves the purpose is appropriate.
      `,
      imageUrl: sowiloImage,
      questions: {
        beginner: {
          question: "How should runes be treated?",
          options: ["Casually", "With care and respect", "As disposable items", "As decorations"],
          correctAnswer: "With care and respect",
        },
        intermediate: {
          question: "What should the rune container do?",
          options: [
            "Protect them from damage and keep them organized",
            "Be expensive and elaborate",
            "Match your home decor",
            "Be made only of traditional materials"
          ],
          correctAnswer: "Protect them from damage and keep them organized",
        },
      },
    },
    {
      id: 'question',
      title: 'IV. Formulating Your Question',
      content: `
        The way you frame your question can significantly affect the quality of the guidance you receive. 
        Questions should be clear, specific, and focused on what you truly want to understand.
        
        Avoid yes/no questions when possible, as runes work better with open-ended questions that allow for nuanced interpretation. 
        Instead of "Will I get the job?" try "What should I focus on to improve my chances of getting this job?"
        
        The question should also be something you're genuinely ready to explore. 
        Runic divination works best when you're open to receiving guidance, even if it's not what you expected or wanted to hear.
      `,
      imageUrl: ansuzImage,
      questions: {
        beginner: {
          question: "What type of questions work best with runes?",
          options: ["Yes/no questions", "Open-ended questions", "Complex mathematical problems", "Historical trivia"],
          correctAnswer: "Open-ended questions",
        },
        intermediate: {
          question: "What should you be ready for when asking questions?",
          options: [
            "Receiving guidance you might not expect",
            "Getting exactly the answer you want",
            "Predictions about the distant future",
            "Simple yes or no answers"
          ],
          correctAnswer: "Receiving guidance you might not expect",
        },
      },
    },
    {
      id: 'timing',
      title: 'V. Timing and Frequency',
      content: `
        There's no strict rule about when to perform runic divination, but many practitioners find that certain times feel more appropriate than others. 
        Some prefer to work during quiet hours, such as early morning or late evening, when there are fewer distractions.
        
        The frequency of readings should be guided by your needs and intuition. 
        It's generally not helpful to ask the same question repeatedly in a short period, as this can lead to confusion rather than clarity.
        
        Trust your intuition about when the time is right for a reading. 
        If you're feeling rushed, stressed, or unclear about your question, it might be better to wait until you're in a better state of mind.
      `,
      imageUrl: berkanoImage,
      questions: {
        beginner: {
          question: "When do many practitioners prefer to work with runes?",
          options: ["During busy hours", "During quiet hours", "Only at midnight", "Only during the day"],
          correctAnswer: "During quiet hours",
        },
        intermediate: {
          question: "What should guide the frequency of readings?",
          options: [
            "Your needs and intuition",
            "A strict schedule",
            "The phase of the moon",
            "The day of the week"
          ],
          correctAnswer: "Your needs and intuition",
        },
      },
    },
  ];

  const [selectedOption, setSelectedOption] = useState('');
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      if (currentQuestion + 1 < sections.length) {
        onQuestionChange(currentQuestion + 1);
      } else {
        setAllQuestionsAnsweredCorrectly(true);
      }
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const getImageStyle = () => {
    const baseStyle = {
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease-in-out'
    };

    if (imageSize === 'smallimage') {
      return {
        ...baseStyle,
        maxWidth: '150px',
        width: '150px'
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: '300px',
        width: '300px'
      };
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>Preparing for Divination - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && sections[currentQuestion]?.imageUrl && (
            <div className="has-text-centered mb-4">
              <img
                src={sections[currentQuestion].imageUrl}
                alt={`Divination Preparation - ${sections[currentQuestion].title}`}
                style={getImageStyle()}
              />
              <div className="icon-buttons mt-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="icon-button has-text-warning mr-3"
                  onClick={toggleImageVisibility}
                  style={{ cursor: 'pointer' }}
                />
                <FontAwesomeIcon
                  icon={faExpandArrowsAlt}
                  className="icon-button has-text-warning"
                  onClick={toggleImageSize}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          )}

          <section className="section has-text-centered" id={sections[currentQuestion]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentQuestion]?.title}</h2>
                  <p className="has-text-white">{sections[currentQuestion]?.content}</p>
                </div>
              </div>
            </div>
          </section>

          {sections[currentQuestion]?.questions && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={sections[currentQuestion].questions[level].question}
                      options={sections[currentQuestion].questions[level].options}
                      correctAnswer={sections[currentQuestion].questions[level].correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RunesPreparationLesson; 